import { useQuery, QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  is_active: boolean;
}

interface ApiMinimalUser {
  id: number;
  first_name: string;
  last_name: string;
  full_name?: string;
  username: string;
  is_active: boolean;
  position?: string;
  [key: string]: any;
}

export const USERS_MINIMAL_KEY = ['users', 'minimal'] as const;

// In-flight promise for deduplication
let inflight: Promise<ApiMinimalUser[]> | null = null;

/**
 * Standalone fetch function — single fetch path for both the hook and services.
 * Includes in-flight deduplication so simultaneous callers share one request.
 */
export async function fetchMinimalUsers(): Promise<ApiMinimalUser[]> {
  if (inflight) return inflight;

  inflight = apiClient
    .get<ApiMinimalUser[]>('/rbac/users/minimal')
    .then((res) => res || [])
    .finally(() => { inflight = null; });

  return inflight;
}

/**
 * Get minimal users from the React Query cache if available,
 * otherwise fetch from the API. Useful for non-hook contexts (services).
 */
export function getCachedMinimalUsers(queryClient: QueryClient): ApiMinimalUser[] | undefined {
  return queryClient.getQueryData<ApiMinimalUser[]>(USERS_MINIMAL_KEY);
}

interface UseUsersOptions {
  enabled?: boolean;
}

export const useUsers = (options?: UseUsersOptions) => {
  const { data, isLoading, error, refetch } = useQuery<User[]>({
    queryKey: USERS_MINIMAL_KEY,
    queryFn: async () => {
      try {
        const raw = await fetchMinimalUsers();
        return raw
          .filter((u) => u.is_active)
          .map((u) => ({
            id: u.id,
            first_name: u.first_name,
            last_name: u.last_name,
            username: u.username,
            email: u.email ?? '',
            is_active: u.is_active,
          }));
      } catch (err) {
        console.error('Failed to fetch users:', err);
        return [
          { id: 1, first_name: 'John', last_name: 'Doe', username: 'john', email: '', is_active: true }
        ];
      }
    },
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const users = data ?? [];

  return {
    users,
    loadingUsers: isLoading,
    error,
    refetch,
  };
};
